/**
 * Minimum-order-quantity resolution.
 *
 * `product_variants.min_order_qty` is nullable and NULL means "inherit from the
 * parent product" — the same inheritance shape `price_tiers` uses (see
 * `tiers.ts`). Every surface that needs a minimum calls `resolveMinOrderQty`:
 * the header comment in `tiers.ts` records that the PDP, the cart and the
 * add-to-cart block drifted apart precisely because each kept its own copy of
 * the tier rule, and this exists so the same thing does not happen here.
 */

/** A minimum that is absent, zero, negative or not a finite number is no minimum: 1. */
function normalise(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

/**
 * The effective minimum order quantity for a line.
 * The variant's own value wins when it has one — including when it is *lower*
 * than the product's, since an explicit override is a deliberate choice.
 */
export function resolveMinOrderQty(
  productMoq: number | null | undefined,
  variantMoq: number | null | undefined,
): number {
  return normalise(variantMoq) ?? normalise(productMoq) ?? 1;
}

/**
 * The lowest `min_qty` in an already-narrowed list of tiers, or null when the
 * list is empty or carries no usable quantity.
 *
 * The narrowing is the caller's job because the two callers hold tiers in
 * different shapes: storefront rows are flat and carry `variant_id` (narrow
 * with `tiersForVariant`), while the admin form keeps product-level tiers in
 * one state array and per-variant overrides on `VariantRow.priceTiers`, neither
 * of which carries a `variant_id`.
 */
export function lowestTierMinQty(
  tiers: Array<{ min_qty: number }>,
): number | null {
  let lowest: number | null = null;
  for (const tier of tiers) {
    const n = normalise(tier.min_qty);
    if (n === null) continue;
    if (lowest === null || n < lowest) lowest = n;
  }
  return lowest;
}
